import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Clock, MapPin, Calendar, XCircle, Loader2 } from 'lucide-react';

export const STATUS_COLOR = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  CONFIRMED: 'bg-blue-100 text-blue-800',
  ACCEPTED: 'bg-blue-100 text-blue-800',
  IN_PROGRESS: 'bg-purple-100 text-purple-800',
  COMPLETED: 'bg-green-100 text-green-800',
  CANCELED: 'bg-red-100 text-red-700',
  CANCELLED: 'bg-red-100 text-red-700',
  NO_SHOW: 'bg-red-100 text-red-700',
  PAID: 'bg-green-100 text-green-800',
  PARTIAL: 'bg-amber-100 text-amber-800',
  UNPAID: 'bg-red-100 text-red-700',
  SENT: 'bg-blue-100 text-blue-800',
};

export const money = (cents) => `$${((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const fmtDateTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString([], {
      weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

export const fmtDate = (s) => {
  if (!s) return '';
  try {
    return new Date(s).toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return s; }
};

export function BookingCard({ b }) {
  return (
    <Card data-testid={`booking-${b.id}`}>
      <CardContent className="pt-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={STATUS_COLOR[b.status] || 'bg-gray-100 text-gray-700'}>{b.status}</Badge>
              {b.serviceName && <span className="font-semibold">{b.serviceName}</span>}
            </div>
            <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
              <Clock className="h-3 w-3" /> {fmtDateTime(b.startDateTime)}
            </p>
            {b.providerName && (
              <p className="text-sm mt-1">
                with{' '}
                {b.providerSlug ? (
                  <Link to={`/providers/${b.providerSlug}`} className="text-primary hover:underline">{b.providerName}</Link>
                ) : (
                  <span className="font-medium">{b.providerName}</span>
                )}
              </p>
            )}
            {b.locationAddress && (
              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                <MapPin className="h-3 w-3" /> {b.locationAddress}
              </p>
            )}
            {b.horseCount > 0 && <p className="text-xs text-muted-foreground mt-1">Animals: {b.horseCount}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function ConsultationCard({ c, navigate }) {
  return (
    <Card data-testid={`consultation-${c.id}`}>
      <CardContent className="pt-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={STATUS_COLOR[c.status] || 'bg-gray-100 text-gray-700'}>{c.status}</Badge>
              <Badge variant="outline">{c.consultationType}</Badge>
            </div>
            {c.providerName && (
              <p className="text-sm mt-2">
                with{' '}
                {c.providerSlug ? (
                  <Link to={`/providers/${c.providerSlug}`} className="text-primary hover:underline">{c.providerName}</Link>
                ) : (
                  <span className="font-medium">{c.providerName}</span>
                )}
              </p>
            )}
            {c.scheduledDate && (
              <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
                <Calendar className="h-3 w-3" /> {fmtDate(c.scheduledDate)}
                {c.scheduledStart && ` • ${c.scheduledStart}`}
                {c.scheduledEnd && ` – ${c.scheduledEnd}`}
              </p>
            )}
          </div>
          <Button size="sm" variant="outline" onClick={() => navigate('/my-consultations')} data-testid={`open-consult-${c.id}`}>
            Open
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function LoadingCard() {
  return (
    <Card>
      <CardContent className="p-8 flex items-center justify-center text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading...
      </CardContent>
    </Card>
  );
}

export function EmptyState({ icon, title, subtitle, cta, onCta }) {
  return (
    <Card>
      <CardContent className="p-8 text-center text-muted-foreground">
        <div className="flex justify-center mb-3">{icon || <XCircle className="h-10 w-10 opacity-40" />}</div>
        <p className="font-medium text-foreground">{title}</p>
        {subtitle && <p className="text-sm mt-1">{subtitle}</p>}
        {cta && (
          <Button className="mt-4" onClick={onCta} data-testid="empty-cta">{cta}</Button>
        )}
      </CardContent>
    </Card>
  );
}
